export interface ReputationExistingReview {
  score: number;
}

export interface ReputationTarget {
  profileId: string;
  displayName: string;
  role: string | null;
  photoUrl: string | null;
  existingReview: ReputationExistingReview | null;
  hasSubmittedReview: boolean;
  hasSubmittedReport: boolean;
}

export interface ReputationSafetySurveyStatus {
  submitted: boolean;
  status: string | null;
}

export interface ReputationSummary {
  enabled: boolean;
  canReview: boolean;
  canReport: boolean;
  canSubmitSafetySurvey: boolean;
  reviewTargets: ReputationTarget[];
  reportTargets: ReputationTarget[];
  submittedReviewTargetIds: string[];
  submittedReportTargetIds: string[];
  safetySurvey: ReputationSafetySurveyStatus | null;
}

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readRecord(value: unknown): JsonRecord | null {
  return isRecord(value) ? value : null;
}

function readString(record: JsonRecord, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function readBoolean(record: JsonRecord, keys: string[], defaultValue = false): boolean {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return defaultValue;
}

function readNumber(record: JsonRecord, keys: string[]): number | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}

function hasRecord(record: JsonRecord, keys: string[]): boolean {
  return keys.some((key) => isRecord(record[key]));
}

function readArray(record: JsonRecord, keys: string[]): unknown[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

function readIdList(record: JsonRecord, keys: string[]): string[] {
  return readArray(record, keys)
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map((value) => value.trim());
}

function readExistingReview(record: JsonRecord): ReputationExistingReview | null {
  const existingReview = readRecord(record.existingReview) ?? readRecord(record.existing_review);
  if (!existingReview) return null;

  const score = readNumber(existingReview, ['score']);
  if (score === null || !Number.isInteger(score) || score < 1 || score > 5) {
    return null;
  }

  return { score };
}

export function normalizeReputationTarget(value: unknown): ReputationTarget | null {
  const record = readRecord(value);
  if (!record) return null;

  const profileId = readString(record, ['profileId', 'profile_id', 'id']);
  const displayName = readString(record, ['displayName', 'display_name', 'name']);

  if (!profileId || !displayName) {
    return null;
  }

  const existingReview = readExistingReview(record);

  return {
    profileId,
    displayName,
    role: readString(record, ['role', 'sessionRole', 'session_role']),
    photoUrl: readString(record, ['photoUrl', 'photo_url', 'avatarUrl', 'avatar_url']),
    existingReview,
    hasSubmittedReview: readBoolean(record, ['hasSubmittedReview', 'has_submitted_review'])
      || Boolean(existingReview)
      || hasRecord(record, ['existingReview', 'existing_review']),
    hasSubmittedReport: readBoolean(record, ['hasSubmittedReport', 'has_submitted_report'])
      || hasRecord(record, ['existingReport', 'existing_report']),
  };
}

function normalizeTargetList(record: JsonRecord, keys: string[]): ReputationTarget[] {
  const seenProfileIds = new Set<string>();

  return readArray(record, keys).reduce<ReputationTarget[]>((targets, value) => {
    const target = normalizeReputationTarget(value);
    if (!target || seenProfileIds.has(target.profileId)) return targets;

    seenProfileIds.add(target.profileId);
    targets.push(target);
    return targets;
  }, []);
}

function normalizeSafetySurvey(record: JsonRecord): ReputationSafetySurveyStatus | null {
  const rawSurvey = readRecord(record.safetySurvey)
    ?? readRecord(record.modelSafetySurvey)
    ?? readRecord(record.model_safety_survey);

  if (!rawSurvey) return null;
  const existingSurvey = readRecord(rawSurvey.existingSurvey)
    ?? readRecord(rawSurvey.existing_survey);

  return {
    submitted: readBoolean(rawSurvey, ['submitted'])
      || Boolean(readString(rawSurvey, ['submittedAt', 'submitted_at']))
      || Boolean(readString(rawSurvey, ['status']))
      || Boolean(existingSurvey),
    status: readString(rawSurvey, ['status']) ?? (existingSurvey ? readString(existingSurvey, ['status']) : null),
  };
}

export function normalizeReputationSummary(payload: unknown): ReputationSummary | null {
  const root = readRecord(payload);
  if (!root) return null;

  const record = readRecord(root.summary) ?? root;
  const reviewTargets = normalizeTargetList(record, ['reviewTargets', 'review_targets']);
  const reportTargets = normalizeTargetList(record, ['reportTargets', 'report_targets']);
  const submittedReviewTargetIds = readIdList(record, [
    'submittedReviewTargetIds',
    'submitted_review_target_ids',
  ]);
  const submittedReportTargetIds = readIdList(record, [
    'submittedReportTargetIds',
    'submitted_report_target_ids',
  ]);
  const safetySurvey = normalizeSafetySurvey(record);
  const canSubmitSafetySurvey = readBoolean(record, [
    'canSubmitSafetySurvey',
    'can_submit_safety_survey',
  ], readBoolean(readRecord(record.safetySurvey) ?? {}, ['canSubmit', 'can_submit']));

  return {
    enabled: readBoolean(record, ['enabled'], true),
    canReview: readBoolean(record, ['canReview', 'can_review'], readBoolean(record, ['canSubmit', 'can_submit'], reviewTargets.length > 0)),
    canReport: readBoolean(record, ['canReport', 'can_report'], readBoolean(record, ['canSubmit', 'can_submit'], reportTargets.length > 0)),
    canSubmitSafetySurvey,
    reviewTargets,
    reportTargets,
    submittedReviewTargetIds,
    submittedReportTargetIds,
    safetySurvey,
  };
}

export function hasVisibleReputationUi(summary: ReputationSummary): boolean {
  if (!summary.enabled) return false;

  const hasReviewTargets = summary.canReview && summary.reviewTargets.length > 0;
  const hasReportTargets = summary.canReport && summary.reportTargets.length > 0;
  const hasSafetySurvey = summary.canSubmitSafetySurvey && !summary.safetySurvey?.submitted;

  return hasReviewTargets || hasReportTargets || hasSafetySurvey;
}
